import { useMemo } from 'react';
import { dashboardApi } from '../../../api/dashboard';
import { useGroup } from '../../../contexts/GroupContext';
import { useGroupQuery } from '../../../hooks/useGroupQuery';
import type { DashboardMetricsResponse } from '../../../types/dashboard';

type UseDashboardSectionDataParams<TMapped> = {
  queryKey: string;
  fallbackGroupId: string;
  selectedGroupOverride?: string | null;
  enabled: (activeGroup: string | null | undefined) => boolean;
  mapData: (data: DashboardMetricsResponse | undefined) => TMapped | null;
};

export function useDashboardSectionData<TMapped>({
  queryKey,
  fallbackGroupId,
  selectedGroupOverride,
  enabled,
  mapData,
}: UseDashboardSectionDataParams<TMapped>) {
  const { selectedGroup: contextGroup } = useGroup();
  const activeGroup = selectedGroupOverride ?? contextGroup;

  const { data, error, isLoading } = useGroupQuery(
    ['dashboard-metrics', queryKey],
    () => dashboardApi.getDashboardMetrics(activeGroup || fallbackGroupId),
    { enabled: enabled(activeGroup) },
  );

  const mappedData = useMemo(() => mapData(data), [data, mapData]);
  const quarters = data?.sections?.[0]?.metrics?.[0]?.series?.length ?? 0;

  return {
    mappedData,
    rawData: data,
    error,
    isLoading,
    quarters,
  };
}
